import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/firebase";

/** Portion of a single day covered by a leave request. */
export type DayPortion = "full" | "first" | "second";

export const DAY_PORTIONS: DayPortion[] = ["full", "first", "second"];

export const DAY_PORTION_LABEL: Record<DayPortion, string> = {
  full: "Full Day",
  first: "First Half",
  second: "Second Half",
};

/** Short label used inside tables/tooltips. */
export const DAY_PORTION_SHORT_LABEL: Record<DayPortion, string> = {
  full: "Full",
  first: "1st Half",
  second: "2nd Half",
};

export type LeaveEntry = {
  fromDate: string;
  toDate: string;
  leaveType: string;
  reason: string;
  numDays?: string;
  approvalStatus: string;
  timestamp: string;
  userId?: string;
  managerIds?: string[];
  /**
   * Per-date portion selection, keyed by yyyy-MM-dd. Absent on legacy entries,
   * which are treated as full days throughout.
   */
  dayPortions?: Record<string, DayPortion>;
  _monthKey?: string;
  _entryId?: string;
};

/** Every date (yyyy-MM-dd) in an inclusive from/to range. */
export const enumerateDateRange = (
  fromDate?: string,
  toDate?: string
): string[] => {
  if (!fromDate || !toDate) return [];
  const start = new Date(fromDate);
  const end = new Date(toDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return [];

  const dates: string[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  // Guard against a pathological range locking up the UI.
  while (cursor <= last && dates.length < 400) {
    dates.push(
      `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(
        cursor.getDate()
      ).padStart(2, "0")}`
    );
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

/** A full day counts as 1, either half counts as 0.5. */
export const dayPortionValue = (portion?: DayPortion): number =>
  portion === "first" || portion === "second" ? 0.5 : 1;

/** Total leave days for a range given its per-day portions. */
export const computeNumDaysFromPortions = (
  dates: string[],
  portions: Record<string, DayPortion>
): number =>
  dates.reduce((sum, d) => sum + dayPortionValue(portions[d]), 0);

/** Render day counts without a trailing ".0" (1, 1.5, 2 …). */
export const formatNumDays = (value: number | string): string => {
  const n = Number(value);
  if (isNaN(n)) return String(value ?? "");
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

/**
 * Human-readable summary of the half-day selections on an entry, e.g.
 * "12-02-2026: First Half". Returns "" when every day is a full day.
 */
export const describeDayPortions = (entry: LeaveEntry): string => {
  const portions = entry.dayPortions;
  if (!portions) return "";
  return Object.keys(portions)
    .filter((d) => portions[d] && portions[d] !== "full")
    .sort()
    .map((d) => {
      const [y, m, day] = d.split("-");
      return `${day}-${m}-${y}: ${DAY_PORTION_LABEL[portions[d]]}`;
    })
    .join(", ");
};

export type LeaveLimits = {
  paidLimit: number;
  sickLimit: number;
  casualLimit: number;
  limitYear?: number;
  /**
   * Extra days an admin granted on top of the check-in accrual, stored as a
   * delta rather than a total so the bonus keeps riding on a growing accrual.
   * Scoped to `addonYear` — a new calendar year starts with no add-on.
   */
  paidAddon: number;
  sickAddon: number;
  casualAddon: number;
  addonYear?: number;
};

export const getMonthKeysForYear = (year: number): string[] => {
  return Array.from({ length: 12 }, (_, i) =>
    `${year}-${String(i + 1).padStart(2, "0")}`
  );
};

export const getMonthKeyFromDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * Fetch all leave entries for a user for a given year (or all years if null).
 * Path: organization/{orgId}/LeaveManagement/{userId}/{YYYY-MM}
 */
export const fetchLeaveEntries = async (
  organizationId: string,
  userId: string,
  year?: number | null
): Promise<LeaveEntry[]> => {
  const currentYear = year ?? new Date().getFullYear();
  const monthKeys = getMonthKeysForYear(currentYear);

  const monthSnapshots = await Promise.all(
    monthKeys.map((mk) =>
      getDocs(
        collection(db, `organization/${organizationId}/LeaveManagement/${userId}/${mk}`)
      )
    )
  );

  const entries: LeaveEntry[] = [];
  monthSnapshots.forEach((snap, idx) => {
    snap.docs.forEach((d) => {
      entries.push({
        ...d.data(),
        _monthKey: monthKeys[idx],
        _entryId: d.id,
      } as LeaveEntry);
    });
  });

  return entries;
};

/**
 * Fetch leave entries for ALL years (2025+).
 */
export const fetchAllLeaveEntries = async (
  organizationId: string,
  userId: string
): Promise<LeaveEntry[]> => {
  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const allEntries: LeaveEntry[] = [];

  for (let y = startYear; y <= currentYear; y++) {
    const yearEntries = await fetchLeaveEntries(organizationId, userId, y);
    allEntries.push(...yearEntries);
  }

  return allEntries;
};

/**
 * Fetch leave entries for multiple users for a given year.
 */
export const fetchLeaveEntriesForUsers = async (
  organizationId: string,
  userIds: string[],
  year?: number
): Promise<Record<string, LeaveEntry[]>> => {
  const currentYear = year ?? new Date().getFullYear();
  const monthKeys = getMonthKeysForYear(currentYear);

  console.log("[leaveUtils] fetchLeaveEntriesForUsers starting", {
    organizationId,
    organizationIdType: typeof organizationId,
    userIdCount: userIds.length,
    currentYear,
    samplePath: userIds[0]
      ? `organization/${organizationId}/LeaveManagement/${userIds[0]}/${monthKeys[0]}`
      : "(no users)",
  });

  const result: Record<string, LeaveEntry[]> = {};
  userIds.forEach((uid) => (result[uid] = []));

  // Each user/month is fetched independently so that one bad request (a
  // stale/malformed user id, a transient network error, etc.) can't reject
  // the whole Promise.all and silently wipe out every other user's leave
  // data for this fetch.
  const allPromises = userIds.flatMap((uid) =>
    monthKeys.map((mk) =>
      getDocs(
        collection(db, `organization/${organizationId}/LeaveManagement/${uid}/${mk}`)
      )
        .then((snap) => ({ uid, mk, snap }))
        .catch((error) => {
          console.error(`❌ Failed to fetch leave entries for user ${uid} (${mk}):`, {
            code: error?.code,
            message: error?.message,
          });
          return null;
        })
    )
  );

  const allResults = await Promise.all(allPromises);
  const failedCount = allResults.filter((r) => r === null).length;
  let totalDocsFound = 0;
  allResults.forEach((entry) => {
    if (!entry) return;
    const { uid, mk, snap } = entry;
    totalDocsFound += snap.docs.length;
    snap.docs.forEach((d) => {
      result[uid].push({
        ...d.data(),
        _monthKey: mk,
        _entryId: d.id,
      } as LeaveEntry);
    });
  });

  console.log("[leaveUtils] fetchLeaveEntriesForUsers finished", {
    totalRequests: allPromises.length,
    failedRequests: failedCount,
    totalDocsFound,
  });

  return result;
};

/**
 * Fetch leave entries for multiple users across ALL years.
 */
export const fetchAllLeaveEntriesForUsers = async (
  organizationId: string,
  userIds: string[]
): Promise<Record<string, LeaveEntry[]>> => {
  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const result: Record<string, LeaveEntry[]> = {};
  userIds.forEach((uid) => (result[uid] = []));

  for (let y = startYear; y <= currentYear; y++) {
    const yearData = await fetchLeaveEntriesForUsers(organizationId, userIds, y);
    userIds.forEach((uid) => {
      result[uid].push(...yearData[uid]);
    });
  }

  return result;
};

/**
 * Fetch leave limits from the new path.
 * Path: organization/{orgId}/LeaveManagement/{userId}
 */
export const fetchLeaveLimits = async (
  organizationId: string,
  userId: string
): Promise<LeaveLimits | null> => {
  const userDocRef = doc(
    db,
    `organization/${organizationId}/LeaveManagement/${userId}`
  );
  const userDocSnap = await getDoc(userDocRef);

  if (userDocSnap.exists()) {
    const data = userDocSnap.data();
    return {
      paidLimit: data.paidLimit ?? 24,
      sickLimit: data.sickLimit ?? 12,
      casualLimit: data.casualLimit ?? 12,
      limitYear: data.limitYear,
      paidAddon: data.paidAddon ?? 0,
      sickAddon: data.sickAddon ?? 0,
      casualAddon: data.casualAddon ?? 0,
      addonYear: data.addonYear,
    };
  }
  return null;
};

/**
 * Save leave limits, stamped with the year they apply to.
 *
 * `limitYear` is what makes limits expire: readers ignore a stored limit whose
 * year is not the one being viewed, so a new calendar year starts from 0.
 * Defaults to the current year when no year is given.
 *
 * `addons` are the extra days granted on top of the accrual. They are written
 * in the same document (as paidAddon/sickAddon/casualAddon) and stamped with
 * the same year, so they expire alongside the limits.
 */
export const saveLeaveLimits = async (
  organizationId: string,
  userId: string,
  limits: { paidLimit: number; sickLimit: number; casualLimit: number },
  limitYear: number = new Date().getFullYear(),
  addons?: { paidAddon: number; sickAddon: number; casualAddon: number }
): Promise<void> => {
  const userDocRef = doc(
    db,
    `organization/${organizationId}/LeaveManagement/${userId}`
  );
  await setDoc(
    userDocRef,
    {
      ...limits,
      limitYear,
      ...(addons ? { ...addons, addonYear: limitYear } : {}),
    },
    { merge: true }
  );
};

/**
 * Submit leave entries to the new path.
 */
export const submitLeaveEntries = async (
  organizationId: string,
  userId: string,
  entries: LeaveEntry[]
): Promise<void> => {
  for (const entry of entries) {
    const entryFromDate = new Date(entry.fromDate);
    const entryMonthKey = `${entryFromDate.getFullYear()}-${String(
      entryFromDate.getMonth() + 1
    ).padStart(2, "0")}`;
    const entryRef = doc(
      collection(
        db,
        `organization/${organizationId}/LeaveManagement/${userId}/${entryMonthKey}`
      )
    );
    await setDoc(entryRef, { ...entry, userId });
  }
};

/**
 * Update a single leave entry's approval status.
 */
export const updateLeaveEntryStatus = async (
  organizationId: string,
  userId: string,
  entry: LeaveEntry,
  newStatus: string
): Promise<void> => {
  if (entry._monthKey && entry._entryId) {
    const entryRef = doc(
      db,
      `organization/${organizationId}/LeaveManagement/${userId}/${entry._monthKey}/${entry._entryId}`
    );
    await updateDoc(entryRef, { approvalStatus: newStatus });
  }
};

/**
 * Delete a single leave entry.
 */
export const deleteLeaveEntry = async (
  organizationId: string,
  userId: string,
  entry: LeaveEntry
): Promise<void> => {
  if (entry._monthKey && entry._entryId) {
    const entryRef = doc(
      db,
      `organization/${organizationId}/LeaveManagement/${userId}/${entry._monthKey}/${entry._entryId}`
    );
    await deleteDoc(entryRef);
  }
};

/**
 * Bulk update all pending leave entries for a user.
 */
export const bulkUpdateLeaveEntries = async (
  organizationId: string,
  userId: string,
  entries: LeaveEntry[],
  currentUserId: string,
  isAdmin: boolean,
  newStatus: string
): Promise<void> => {
  const batch = writeBatch(db);

  for (const entry of entries) {
    const status = (entry.approvalStatus || "Pending") === "Pending";
    const include =
      status &&
      (isAdmin ||
        (Array.isArray(entry.managerIds) &&
          entry.managerIds.includes(currentUserId)));

    if (include && entry._monthKey && entry._entryId) {
      const entryRef = doc(
        db,
        `organization/${organizationId}/LeaveManagement/${userId}/${entry._monthKey}/${entry._entryId}`
      );
      batch.update(entryRef, { approvalStatus: newStatus });
    }
  }

  await batch.commit();
};
